import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getDashboard } from './businessApi';
import TechLoadingBar from '../../components/ui/TechLoadingBar';

const BASE = process.env.REACT_APP_API_URL || '/api';

const BIZ_PACKS = [
  { id: 'biz_1000',  label: '1,000',  price: '€4.99'  },
  { id: 'biz_5000',  label: '5,000',  price: '€19.99', popular: true },
  { id: 'biz_10000', label: '10,000', price: '€34.99' },
  { id: 'biz_50000', label: '50,000', price: '€149.99' },
];

function BizWalletCard({ nl, businessId, currentUserId, walletBalance, monthlyUsed, role }) {
  const [buying, setBuying]   = useState(null);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(false);
  const canManage = ['owner', 'admin'].includes(role);

  const buyPack = async (packId) => {
    if (!canManage) return;
    setBuying(packId);
    setError(null);
    try {
      const res = await fetch(`${BASE}/businesses/${businessId}/wallet/topup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packId, uid: currentUserId }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; }
      else setError(data.error || (nl ? 'Fout bij betaling.' : 'Payment error.'));
    } catch { setError(nl ? 'Verbindingsfout.' : 'Connection error.'); }
    finally { setBuying(null); }
  };

  const accent = _T.accent;
  const usedPct = walletBalance + monthlyUsed > 0 ? Math.round((monthlyUsed / (walletBalance + monthlyUsed)) * 100) : 0;

  return (
    <div style={{ background: `linear-gradient(135deg,${accent}18,${_T.bg3})`, border: `1px solid ${accent}33`, borderRadius: 16, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 9, color: accent, letterSpacing: 3, fontWeight: 700, marginBottom: 2 }}>
            {nl ? 'BEDRIJFS CREDITS' : 'BUSINESS CREDITS'}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>{(walletBalance || 0).toLocaleString()}</span>
            <span style={{ fontSize: 11, color: C.silver }}>{nl ? 'beschikbaar' : 'available'}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 9, color: C.silver, marginBottom: 2 }}>{nl ? 'Gebruikt deze maand' : 'Used this month'}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.orange }}>{(monthlyUsed || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Usage bar */}
      {(walletBalance + monthlyUsed) > 0 && (
        <div style={{ height: 4, background: `${accent}15`, borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ height: '100%', width: `${Math.min(100, usedPct)}%`, background: `linear-gradient(90deg,${C.orange},${C.red})`, borderRadius: 3, transition: 'width 0.6s' }} />
        </div>
      )}

      {canManage && (
        <>
          <button
            onClick={() => setExpanded(v => !v)}
            style={{ background: 'none', border: `1px solid ${accent}55`, borderRadius: 10, color: accent, fontSize: 11, fontWeight: 700, padding: '6px 14px', cursor: 'pointer', width: '100%' }}
          >
            {expanded ? (nl ? '▲ Sluiten' : '▲ Close') : (nl ? '▼ Credits kopen' : '▼ Buy Credits')}
          </button>
          {expanded && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {BIZ_PACKS.map(pack => (
                  <button
                    key={pack.id}
                    onClick={() => buyPack(pack.id)}
                    disabled={!!buying}
                    style={{
                      position: 'relative',
                      background: pack.popular ? `${accent}25` : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${pack.popular ? accent + '88' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: 12, padding: '10px 8px', cursor: buying ? 'not-allowed' : 'pointer',
                      opacity: buying && buying !== pack.id ? 0.6 : 1, textAlign: 'center',
                    }}
                  >
                    {pack.popular && (
                      <div style={{ position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)', background: accent, color: '#0a1220', fontSize: 8, fontWeight: 800, letterSpacing: 1.5, padding: '2px 8px', borderRadius: 20 }}>
                        {nl ? 'POPULAIR' : 'POPULAR'}
                      </div>
                    )}
                    <div style={{ fontSize: 12, fontWeight: 700, color: pack.popular ? accent : '#fff' }}>{pack.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{buying === pack.id ? '...' : pack.price}</div>
                  </button>
                ))}
              </div>
              {error && <div style={{ color: C.red, fontSize: 11, marginTop: 8, textAlign: 'center' }}>{error}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export const INDUSTRIES = [
  { id: 'healthcare',   icon: '🏥', nl: 'Zorg / Medisch',        en: 'Healthcare / Medical' },
  { id: 'retail',          icon: '🛍️',  nl: 'Retail / Winkel',        en: 'Retail / Shop' },
  { id: 'retail_clothing', icon: '👗',  nl: 'Kledingwinkel',           en: 'Clothing / Fashion' },
  { id: 'restaurant',   icon: '🍽️', nl: 'Horeca / F&B',           en: 'Restaurant / Food & Beverage' },
  { id: 'technology',   icon: '💻', nl: 'Technologie / IT',        en: 'Technology / IT' },
  { id: 'construction', icon: '🏗️', nl: 'Bouw / Vastgoed',         en: 'Construction / Real Estate' },
  { id: 'education',    icon: '🎓', nl: 'Onderwijs',               en: 'Education' },
  { id: 'fitness',      icon: '⚽', nl: 'Sport / Fitness',          en: 'Fitness / Sports' },
  { id: 'consulting',   icon: '📊', nl: 'Consultancy / Advies',    en: 'Consulting / Professional Services' },
  { id: 'creative',     icon: '🎨', nl: 'Creatief / Media',         en: 'Creative / Media' },
  { id: 'logistics',    icon: '🏭', nl: 'Productie / Logistiek',   en: 'Manufacturing / Logistics' },
  { id: 'software',     icon: '📦', nl: 'Software / Digitale producten', en: 'Software / Digital Products' },
  { id: 'other',        icon: '🏢', nl: 'Overig',                  en: 'Other' },
];

const INDUSTRY_CONFIG = {
  healthcare:   { risk_label: { nl: 'Dienst vermoeidheid', en: 'Shift fatigue' },        focus: 'sleep', tip: { nl: 'Zorgpersoneel heeft 35% meer kans op burnout. Houd slaap & emotionele belasting goed bij.', en: 'Healthcare workers are 35% more likely to experience burnout. Monitor sleep & emotional load closely.' }, accent: '#2A9E82' },
  retail:       { risk_label: { nl: 'Weekendstress',       en: 'Weekend stress' },        focus: 'mood',  tip: { nl: 'Klantcontact verhoogt emotionele vermoeidheid. Plan rustmomenten na drukke diensten in.', en: 'Customer-facing roles increase emotional fatigue. Schedule recovery after busy shifts.' }, accent: '#5B9ED0' },
  restaurant:   { risk_label: { nl: 'Late dienst herstel', en: 'Late shift recovery' },   focus: 'sleep', tip: { nl: 'Avonddiensten verstoren het bioritme. Stimuleer ploegenwisseling met recuperatietijd.', en: 'Evening shifts disrupt circadian rhythms. Encourage rotation with adequate recovery time.' }, accent: '#D4873A' },
  technology:   { risk_label: { nl: 'Mentale belasting',   en: 'Mental overload' },       focus: 'focus', tip: { nl: 'Diepe werksessies van 90 min. + 15 min. pauze verhogen output met 40%.', en: '90-min deep work blocks + 15-min breaks increase output by 40%. Protect flow states.' }, accent: '#7B6FD0' },
  construction: { risk_label: { nl: 'Fysieke belasting',   en: 'Physical strain' },       focus: 'sleep', tip: { nl: 'Fysieke vermoeidheid verhoogt het ongelukrisico met 70%. Voldoende slaap is veiligheid.', en: 'Physical fatigue increases accident risk by 70%. Adequate sleep is a safety metric.' }, accent: '#C8A84B' },
  education:    { risk_label: { nl: 'Termijn vermoeidheid', en: 'Term fatigue' },         focus: 'mood',  tip: { nl: 'Leraren ervaren pieken in stress tijdens toetsperiodes. Plan welzijnscheck-ins strategisch.', en: 'Teachers experience stress peaks during exam periods. Time wellness check-ins strategically.' }, accent: '#5B9ED0' },
  fitness:      { risk_label: { nl: 'Overtraining signaal', en: 'Overtraining signal' },  focus: 'sleep', tip: { nl: 'Fitnessprofessionals trainen zichzelf vaak te hard. Herstelkwaliteit is net zo belangrijk.', en: 'Fitness professionals often overtrain themselves. Recovery quality matters as much as effort.' }, accent: '#4AC87A' },
  consulting:   { risk_label: { nl: 'Deadline druk',       en: 'Deadline pressure' },     focus: 'focus', tip: { nl: 'Consultants werken gemiddeld 50+ uur per week. Mentale reset-rituelen zijn essentieel.', en: 'Consultants average 50+ hour weeks. Mental reset rituals are non-negotiable for longevity.' }, accent: '#C8A84B' },
  creative:     { risk_label: { nl: 'Creatieve blokkade',  en: 'Creative block' },        focus: 'mood',  tip: { nl: 'Creativiteit daalt sterk bij slaaptekort. Bescherm de avondroutine van je team.', en: 'Creativity drops sharply with sleep debt. Protect your team\'s evening wind-down routine.' }, accent: '#C05050' },
  logistics:        { risk_label: { nl: 'Dienst rotatie',      en: 'Shift rotation' },        focus: 'sleep', tip: { nl: 'Ploegenarbeid verstoort circadiane ritmes. Zorg voor consistente slaapvensters per ploeg.', en: 'Shift work disrupts circadian rhythms. Ensure consistent sleep windows per rotation.' }, accent: '#D4873A' },
  retail_clothing:  { risk_label: { nl: 'Weekendstress',       en: 'Weekend stress' },        focus: 'mood',  tip: { nl: 'Klantgerichte kledingretail vraagt emotionele energie. Bouw herstelrituelen in na drukke diensten.', en: 'Customer-facing fashion retail demands emotional energy. Build recovery rituals after busy shifts.' }, accent: '#C05A8A' },
  software:         { risk_label: { nl: 'Release druk',       en: 'Release pressure' },      focus: 'focus', tip: { nl: 'Softwaredevelopers hebben moeite los te koppelen van werk. Bescherm avondroutines en plan recovery na sprints.', en: 'Software developers struggle to disconnect from work. Protect evening routines and schedule recovery after sprint cycles.' }, accent: '#3b82f6' },
  other:            { risk_label: { nl: 'Hoog risico',         en: 'High risk' },             focus: 'sleep', tip: { nl: 'Teams met voldoende slaap (7-9u) presteren 20% beter. Maak welzijn een bedrijfsprioriteit.', en: 'Teams with adequate sleep (7-9h) perform 20% better. Make wellness a business priority.' }, accent: '#7B6FD0' },
};

function getTeamWellnessScore(active) {
  if (!active.length) return null;
  const total = active.reduce((acc, m) => {
    const sleepScore = Math.min(40, ((m.avg_sleep || 5) / 10) * 40);
    const moodScore  = Math.min(40, ((m.avg_mood  || 2.5) / 5) * 40);
    const riskScore  = m.burnout_risk === 'high' ? 0 : m.burnout_risk === 'medium' ? 10 : 20;
    return acc + sleepScore + moodScore + riskScore;
  }, 0);
  return Math.round(total / active.length);
}

function ScoreRing({ score, size = 80 }) {
  if (score === null) return null;
  const color = score >= 70 ? C.teal : score >= 50 ? C.orange : C.red;
  const label = score >= 70 ? '✓' : score >= 50 ? '~' : '!';
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: `conic-gradient(${color} ${score * 3.6}deg, ${color}22 0deg)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
      <div style={{ width: size - 16, height: size - 16, borderRadius: '50%', background: _T.bg3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: size * 0.28, fontWeight: 800, color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: size * 0.12, color: C.silver, marginTop: 1 }}>{label}</div>
      </div>
    </div>
  );
}

function MemberRow({ m, nl, cfg }) {
  const risk   = m.burnout_risk || 'low';
  const rColor = risk === 'high' ? C.red : risk === 'medium' ? C.orange : C.teal;
  const initial = (m.name || '?').charAt(0).toUpperCase();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: risk === 'high' ? `${C.red}08` : _T.bg4, borderRadius: 12, border: `1px solid ${risk === 'high' ? C.red + '33' : _T.cardBorderBot}` }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${rColor}20`, border: `2px solid ${rColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: rColor, flexShrink: 0 }}>
        {m.picture ? <img src={m.picture} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} /> : initial}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: C.white, fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || (nl ? 'Lid' : 'Member')}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
          <span style={{ fontSize: 10, color: C.silver }}>😴 {m.avg_sleep != null ? m.avg_sleep.toFixed(1) : '—'}</span>
          <span style={{ fontSize: 10, color: C.silver }}>😊 {m.avg_mood  != null ? m.avg_mood.toFixed(1)  : '—'}</span>
        </div>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontSize: 9, color: rColor, fontWeight: 700, background: `${rColor}15`, border: `1px solid ${rColor}33`, borderRadius: 8, padding: '2px 7px' }}>
          {risk === 'high' ? (nl ? 'HOOG' : 'HIGH') : risk === 'medium' ? (nl ? 'MIDDEL' : 'MED') : (nl ? 'LAAG' : 'LOW')}
        </div>
      </div>
    </div>
  );
}

function TrendBar({ label, value, max, color, icon }) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ color: C.silver, fontSize: 11 }}>{icon} {label}</span>
        <span style={{ color: C.white, fontSize: 11, fontWeight: 700 }}>{value != null ? value.toFixed(1) : '—'} <span style={{ color: C.silver, fontWeight: 400 }}>/ {max}</span></span>
      </div>
      <div style={{ height: 6, background: `${color}20`, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${color}88,${color})`, borderRadius: 3, transition: 'width 0.7s ease' }} />
      </div>
    </div>
  );
}

function BurnoutTimeline({ members, nl }) {
  // Predict weeks-to-burnout based on trend slope
  const predictions = members
    .filter(m => m.avg_sleep != null || m.avg_mood != null)
    .map(m => {
      const currSleep = m.avg_sleep ?? 7;
      const prevSleep = m.prev_sleep ?? currSleep;
      const currMood  = m.avg_mood  ?? 3.5;
      const prevMood  = m.prev_mood  ?? currMood;
      const sleepDelta = currSleep - prevSleep; // per 2 weeks
      const moodDelta  = currMood  - prevMood;
      const risk = m.burnout_risk || 'low';

      // How many 2-week periods until sleep drops below 5 or mood below 2.5
      let weeksToHigh = null;
      if (sleepDelta < 0 && currSleep > 5) {
        const periods = (currSleep - 5) / Math.abs(sleepDelta);
        weeksToHigh = Math.round(periods * 2);
      }
      if (moodDelta < 0 && currMood > 2.5) {
        const periods = (currMood - 2.5) / Math.abs(moodDelta);
        const w = Math.round(periods * 2);
        weeksToHigh = weeksToHigh === null ? w : Math.min(weeksToHigh, w);
      }
      return { name: m.name, risk, sleepDelta, moodDelta, weeksToHigh, currSleep, currMood };
    })
    .filter(p => p.risk !== 'low' || (p.weeksToHigh !== null && p.weeksToHigh <= 8))
    .sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      return (order[a.risk] ?? 2) - (order[b.risk] ?? 2);
    });

  if (predictions.length === 0) return null;

  return (
    <div style={{ background: _T.bg3, border: `1px solid ${C.red}33`, borderRadius: 16, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 9, color: C.red, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>
        {nl ? 'BURNOUT VOORSPELLING TIJDLIJN' : 'BURNOUT PREDICTION TIMELINE'}
      </div>
      {predictions.map((p, i) => {
        const rColor = p.risk === 'high' ? C.red : p.risk === 'medium' ? C.orange : C.teal;
        const trendText = p.weeksToHigh !== null
          ? (nl ? `⚠️ Hoog risico in ~${p.weeksToHigh} weken bij huidige trend` : `⚠️ High risk in ~${p.weeksToHigh} weeks at current trend`)
          : (p.risk === 'high' ? (nl ? '🚨 Momenteel hoog risico' : '🚨 Currently high risk') : null);
        return (
          <div key={i} style={{ marginBottom: i < predictions.length - 1 ? 10 : 0, paddingBottom: i < predictions.length - 1 ? 10 : 0, borderBottom: i < predictions.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: C.white, fontWeight: 600 }}>{p.name}</span>
              <span style={{ fontSize: 9, color: rColor, background: `${rColor}15`, padding: '2px 7px', borderRadius: 6, fontWeight: 700 }}>
                {p.risk.toUpperCase()}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
              <span style={{ fontSize: 10, color: C.silver }}>
                😴 {p.currSleep.toFixed(1)} {p.sleepDelta !== 0 ? <span style={{ color: p.sleepDelta > 0 ? C.teal : C.red }}>{p.sleepDelta > 0 ? '↑' : '↓'}{Math.abs(p.sleepDelta).toFixed(1)}</span> : '→'}
              </span>
              <span style={{ fontSize: 10, color: C.silver }}>
                😊 {p.currMood.toFixed(1)} {p.moodDelta !== 0 ? <span style={{ color: p.moodDelta > 0 ? C.teal : C.red }}>{p.moodDelta > 0 ? '↑' : '↓'}{Math.abs(p.moodDelta).toFixed(1)}</span> : '→'}
              </span>
            </div>
            {trendText && <div style={{ fontSize: 10, color: p.risk === 'high' ? C.red : C.orange, marginTop: 4 }}>{trendText}</div>}
            {p.weeksToHigh !== null && (
              <div style={{ marginTop: 6, height: 4, background: `${C.red}15`, borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.max(5, 100 - (p.weeksToHigh / 12) * 100)}%`, background: `linear-gradient(90deg,${C.red},${C.orange})`, borderRadius: 3 }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function BusinessDashboard({ lang, businessId, business, currentUserId, role, onSwitchTab }) {
  const nl = lang === 'nl';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const industry = business?.industry || 'other';
  const cfg = INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.other;
  const ind = INDUSTRIES.find(i => i.id === industry) || INDUSTRIES[INDUSTRIES.length - 1];

  useEffect(() => {
    getDashboard(businessId).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    const iv = setInterval(() => getDashboard(businessId).then(d => setData(d)).catch(() => {}), 30 * 1000);
    return () => clearInterval(iv);
  }, [businessId]);

  if (loading) return <TechLoadingBar lang={lang} />;

  const members    = Array.isArray(data?.members) ? data.members : [];
  // Dashboard API only returns active members, so treat all returned entries as active
  const active     = members.filter(m => !m.status || m.status === 'active');
  const highRisk   = active.filter(m => m.burnout_risk === 'high');
  const medRisk    = active.filter(m => m.burnout_risk === 'medium');
  const avgSleep   = active.length ? active.reduce((a, m) => a + (m.avg_sleep || 0), 0) / active.length : null;
  const avgMood    = active.length ? active.reduce((a, m) => a + (m.avg_mood  || 0), 0) / active.length : null;
  const prevSleep  = active.length ? active.reduce((a, m) => a + (m.prev_sleep || 0), 0) / active.length : null;
  const prevMood   = active.length ? active.reduce((a, m) => a + (m.prev_mood  || 0), 0) / active.length : null;
  const checkinRate = data?.checkin_rate ?? null;
  const checkedIn    = data?.checked_in ?? null;
  const totalMembers = data?.total_members ?? active.length;
  const walletBal    = data?.wallet_balance ?? 0;
  const monthlyUsed  = data?.wallet_monthly_used ?? 0;
  const score      = getTeamWellnessScore(active);
  const scoreColor = score === null ? C.silver : score >= 70 ? C.teal : score >= 50 ? C.orange : C.red;

  const noData = active.length === 0;

  // Trend helpers
  const trendArrow = (curr, prev) => {
    if (curr == null || prev == null || prev === 0) return null;
    const diff = curr - prev;
    if (Math.abs(diff) < 0.1) return { icon: '→', color: C.silver };
    return diff > 0 ? { icon: '↑', color: C.teal } : { icon: '↓', color: C.red };
  };
  const sleepTrend = trendArrow(avgSleep, prevSleep);
  const moodTrend  = trendArrow(avgMood, prevMood);

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Industry header banner */}
      <div style={{ background: `linear-gradient(135deg,${cfg.accent}18,${_T.bg3})`, border: `1px solid ${cfg.accent}33`, borderRadius: 18, padding: '16px', marginBottom: 14, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: `${cfg.accent}10`, pointerEvents: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: noData ? 0 : 14 }}>
          {business?.logo_url
            ? <img src={business.logo_url} alt="" style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover', border: `2px solid ${cfg.accent}44`, flexShrink: 0 }} />
            : <div style={{ width: 48, height: 48, borderRadius: 12, background: `${cfg.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{ind.icon}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, color: cfg.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 2 }}>{nl ? ind.nl.toUpperCase() : ind.en.toUpperCase()}</div>
            <div style={{ color: C.white, fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{business?.name || 'Business'}</div>
          </div>
          {!noData && <ScoreRing score={score} size={64} />}
        </div>
        {!noData && (
          <div style={{ fontSize: 10, color: C.silver, lineHeight: 1.5, borderTop: `1px solid ${cfg.accent}22`, paddingTop: 10 }}>
            <span style={{ color: cfg.accent, fontWeight: 600 }}>{ind.icon} {nl ? 'Inzicht: ' : 'Insight: '}</span>{nl ? cfg.tip.nl : cfg.tip.en}
          </div>
        )}
      </div>

      {/* No members state */}
      {noData ? (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 18, padding: '36px 24px', textAlign: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <div style={{ color: C.white, fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Nog geen teamleden' : 'No team members yet'}</div>
          <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
            {nl ? 'Nodig teamleden uit om hun welzijn te volgen en je bedrijfsdashboard te activeren.' : 'Invite team members to track their wellness and activate your business dashboard.'}
          </div>
          {onSwitchTab && (
            <button onClick={() => onSwitchTab('members')}
              style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '11px 24px', cursor: 'pointer' }}>
              {nl ? '+ Teamlid uitnodigen' : '+ Invite team member'}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Business wallet */}
          <BizWalletCard
            nl={nl}
            businessId={businessId}
            currentUserId={currentUserId}
            walletBalance={walletBal}
            monthlyUsed={monthlyUsed}
            role={role}
          />

          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
            {[
              { icon: '👥', label: nl ? 'Team' : 'Team',      val: active.length, sub: null, color: cfg.accent },
              { icon: '😴', label: nl ? 'Slaap' : 'Sleep',    val: avgSleep != null ? avgSleep.toFixed(1) : '—', trend: sleepTrend, color: _T.accent },
              { icon: '😊', label: nl ? 'Humeur' : 'Mood',    val: avgMood  != null ? avgMood.toFixed(1)  : '—', trend: moodTrend, color: C.teal },
              { icon: '⚠️', label: nl ? 'Risico' : 'At risk', val: highRisk.length, sub: null, color: highRisk.length > 0 ? C.red : C.teal },
            ].map(s => (
              <div key={s.label} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '10px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: 18, marginBottom: 2 }}>{s.icon}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                  <span style={{ color: s.color, fontSize: 16, fontWeight: 700 }}>{s.val}</span>
                  {s.trend && <span style={{ color: s.trend.color, fontSize: 12, fontWeight: 700 }}>{s.trend.icon}</span>}
                </div>
                <div style={{ color: C.silver, fontSize: 8, marginTop: 1 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Check-in Response Rate */}
          {checkinRate !== null && (
            <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '12px 16px', marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 9, color: _T.accent, letterSpacing: 2, fontWeight: 700 }}>{nl ? 'CHECK-IN RESPONS DEZE WEEK' : 'CHECK-IN RESPONSE THIS WEEK'}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: checkinRate >= 70 ? C.teal : checkinRate >= 40 ? C.orange : C.red }}>{checkinRate}%</span>
              </div>
              <div style={{ height: 6, background: `${_T.accent}15`, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${checkinRate}%`, background: checkinRate >= 70 ? `linear-gradient(90deg,${C.teal},${_T.accent})` : checkinRate >= 40 ? `linear-gradient(90deg,${C.orange},${C.orange})` : `linear-gradient(90deg,${C.red},${C.orange})`, borderRadius: 3, transition: 'width 0.6s' }} />
              </div>
              <div style={{ color: C.silver, fontSize: 10, marginTop: 5 }}>{checkedIn} / {totalMembers} {nl ? 'ingecheckt' : 'checked in'}</div>
            </div>
          )}

          {/* Burnout prediction timeline */}
          <BurnoutTimeline members={active} nl={nl} />

          {/* Team Wellness Score card */}
          <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '16px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <ScoreRing score={score} size={72} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 6 }}>{nl ? 'TEAM WELLNESS SCORE' : 'TEAM WELLNESS SCORE'}</div>
                <TrendBar label={nl ? 'Gem. slaap' : 'Avg sleep'} value={avgSleep} max={10} color={_T.accent} icon="😴" />
                <TrendBar label={nl ? 'Gem. humeur' : 'Avg mood'}  value={avgMood}  max={5}  color={C.teal}    icon="😊" />
                {sleepTrend && (
                  <div style={{ fontSize: 10, color: sleepTrend.color, marginTop: 4 }}>
                    {sleepTrend.icon} {nl ? 'vs vorige 2 weken' : 'vs previous 2 weeks'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* High risk alert */}
          {highRisk.length > 0 && (
            <div style={{ background: `${C.red}10`, border: `1px solid ${C.red}44`, borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 16 }}>🚨</span>
                <div style={{ fontSize: 11, color: C.red, fontWeight: 700, letterSpacing: 1 }}>{nl ? `${highRisk.length} TEAMLID${highRisk.length > 1 ? 'EN' : ''} MET HOOG BURNOUT-RISICO` : `${highRisk.length} TEAM MEMBER${highRisk.length > 1 ? 'S' : ''} AT HIGH BURNOUT RISK`}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {highRisk.map((m, i) => <MemberRow key={m.user_id || i} m={m} nl={nl} cfg={cfg} />)}
              </div>
            </div>
          )}

          {/* Medium risk */}
          {medRisk.length > 0 && (
            <div style={{ background: `${C.orange}08`, border: `1px solid ${C.orange}33`, borderRadius: 14, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: C.orange, fontWeight: 700, letterSpacing: 2, marginBottom: 8 }}>{nl ? 'VERHOOGD RISICO' : 'ELEVATED RISK'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {medRisk.map((m, i) => <MemberRow key={m.user_id || i} m={m} nl={nl} cfg={cfg} />)}
              </div>
            </div>
          )}

          {/* Full team */}
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>{nl ? 'HEEL HET TEAM' : 'FULL TEAM'} ({active.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {active.map((m, i) => (
              <MemberRow key={m.user_id || i} m={m} nl={nl} cfg={cfg} />
            ))}
            {active.length === 0 && (
              <div style={{ color: C.silver, fontSize: 12, textAlign: 'center', padding: '8px 0' }}>
                {nl ? 'Nog geen teamleden.' : 'No team members yet.'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
